import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { IUserWaitlistSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import { Modal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";
import CancelRegistrationModal from "src/routes/Account/UserWaitlistTab/CancelRegistrationModal";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./RecentEntries.module.scss";

const SECTION_SHOW_TYPES = [
    Constants.show_type.nosework,
    Constants.show_type.herding,
    Constants.show_type.country_rat_race,
];

const showTypeUsesSection = (showType: string, sanctioningClub: string): boolean => {
    if (SECTION_SHOW_TYPES.includes(showType)) return true;
    if (
        sanctioningClub === Constants.sanctioning_club.C_WAGS &&
        (showType === Constants.show_type.obedience || showType === Constants.show_type.rally_obedience)
    ) return true;
    return false;
};

const RecentEntries: React.FC = () => {
    const user = helperService.getUser();

    const [entries, setEntries] = useState<IUserWaitlistSummary[]>([]);
    const [cancelTarget, setCancelTarget] = useState<IUserWaitlistSummary | null>(null);
    const [withdrawTarget, setWithdrawTarget] = useState<IUserWaitlistSummary | null>(null);
    const [withdrawing, setWithdrawing] = useState(false);

    const fetchEntries = useCallback(() => {
        if (!user._id) return;
        apiService.getUserRegistrations(user._id, {
            sort: [{ attribute_name: "show_date", sort: "desc" }],
        } as any).then((data) => {
            setEntries((data || []).slice(0, 5));
        }).catch(() => {
            // silently fail — sidebar is non-critical
        });
    }, [user._id]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    if (entries.length === 0) return null;

    const isPastShowDate = (entry: IUserWaitlistSummary): boolean =>
        new Date(entry.show_date) < new Date();

    const canCancel = (entry: IUserWaitlistSummary): boolean => {
        if (isPastShowDate(entry)) return false;
        return (
            entry.status === Constants.waitlist_status.registered ||
            entry.status === Constants.waitlist_status.refund_denied ||
            entry.status === Constants.waitlist_status.waitlist
        );
    };

    const getStatusLabel = (entry: IUserWaitlistSummary): string => {
        switch (entry.status) {
            case Constants.waitlist_status.registered:
                return "Registered";
            case Constants.waitlist_status.waitlist:
                return `Waitlist${entry.position ? ` #${entry.position}` : ""}`;
            case Constants.waitlist_status.refund_denied:
                return "Refund Denied";
            default:
                return entry.status ?? "";
        }
    };

    const getStatusClass = (entry: IUserWaitlistSummary): string => {
        switch (entry.status) {
            case Constants.waitlist_status.registered:
                return styles.statusRegistered;
            case Constants.waitlist_status.waitlist:
                return styles.statusWaitlist;
            case Constants.waitlist_status.refund_denied:
                return styles.statusDenied;
            default:
                return styles.statusDefault;
        }
    };

    const handleWithdrawConfirm = async () => {
        if (!withdrawTarget) return;
        setWithdrawing(true);
        try {
            await apiService.cancelUserWaitlistEntry(withdrawTarget._id);
            toast.show({
                title: "Withdrawn",
                content: `${withdrawTarget.call_name} has been withdrawn from the waitlist for ${withdrawTarget.show_name}.`,
                duration: 8000,
                type: "success",
            });
            setWithdrawTarget(null);
            fetchEntries();
        } catch (e: any) {
            toast.show({
                title: "Withdraw Failed",
                content: e?.response?.data?.message ?? "Something went wrong. Please try again.",
                duration: 8000,
                type: "fail",
            });
        } finally {
            setWithdrawing(false);
        }
    };

    return (
        <div className={styles.RecentEntries}>
            <div className={styles.title}>Recent Entries</div>
            <ul className={styles.list}>
                {entries.map((entry) => {
                    const past = isPastShowDate(entry);
                    const cancellable = canCancel(entry);
                    const isWaitlist = entry.status === Constants.waitlist_status.waitlist;

                    return (
                        <li key={`${entry._id}-${entry.dog_class?.show_element}-${entry.dog_class?.level}`} className={styles.item}>
                            <div className={styles.inner}>
                                {/* Row 1: Sanctioning club + show type (left) + status pill (right) */}
                                <div className={styles.typeRow}>
                                    <span className={styles.typeLabel}>
                                        {[entry.sanctioning_club, entry.show_type].filter(Boolean).join(" ")}
                                    </span>
                                    <span className={`${styles.statusPill} ${getStatusClass(entry)}`}>
                                        {getStatusLabel(entry)}
                                    </span>
                                </div>

                                {/* Row 2: Event name (clickable, left) + show name (right) */}
                                <div className={styles.eventRow}>
                                    {entry.event?.handle ? (
                                        <Link to={`/e/${entry.event.handle}`} className={styles.eventName}>
                                            {entry.event.title || "—"}
                                        </Link>
                                    ) : (
                                        <span className={styles.eventName}>{entry.event?.title || "—"}</span>
                                    )}
                                    <span className={styles.showName}>{entry.show_name}</span>
                                </div>

                                {/* Row 3: Dog name (left) + date (right) */}
                                <div className={styles.dogRow}>
                                    <span className={styles.dogName}>{entry.call_name}</span>
                                    <span className={styles.showDate}>
                                        {moment(entry.show_date).format("MM/DD/YYYY")}
                                    </span>
                                </div>

                                {/* Row 4: class chips — element (if not same as show type), level, section */}
                                {(entry.dog_class?.show_element || entry.dog_class?.level) && (
                                    <div className={styles.detailLine}>
                                        {entry.dog_class?.show_element &&
                                            entry.dog_class.show_element.toLowerCase() !== (entry.show_type || "").toLowerCase() && (
                                            <span className={styles.chip}>{entry.dog_class.show_element}</span>
                                        )}
                                        {entry.dog_class?.level && (
                                            <span className={styles.chip}>{entry.dog_class.level}</span>
                                        )}
                                        {entry.dog_class?.section &&
                                            entry.dog_class.section !== "None" &&
                                            showTypeUsesSection(entry.show_type, entry.sanctioning_club) && (
                                            <span className={styles.chip}>{entry.dog_class.section}</span>
                                        )}
                                    </div>
                                )}

                                {/* Footer: cancel / withdraw CTA */}
                                <div className={styles.tileFooter}>
                                    {cancellable ? (
                                        <button
                                            className={styles.cancelBtn}
                                            onClick={() =>
                                                isWaitlist
                                                    ? setWithdrawTarget(entry)
                                                    : setCancelTarget(entry)
                                            }
                                        >
                                            {isWaitlist ? "Withdraw →" : "Cancel →"}
                                        </button>
                                    ) : past ? (
                                        <span className={styles.cancelDisabled}>Past entry</span>
                                    ) : null}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>

            <div className={styles.viewAll}>
                <Link to="/account?t=1">View All Entries →</Link>
            </div>

            {/* Cancel registered entry modal */}
            {cancelTarget && (
                <Modal
                    isShown={!!cancelTarget}
                    hide={() => setCancelTarget(null)}
                    modalContent={
                        <CancelRegistrationModal
                            entry={{
                                show_registration_id: (cancelTarget as any).show_registration_id ?? cancelTarget._id,
                                call_name: cancelTarget.call_name ?? "",
                                show_type: cancelTarget.show_type,
                                show_name: cancelTarget.show_name,
                                show_date: String(cancelTarget.show_date),
                                show_element: cancelTarget.dog_class?.show_element ?? "",
                                level: cancelTarget.dog_class?.level,
                                section: cancelTarget.dog_class?.section,
                                entry_fee: null,
                                entry_platform_fee: null,
                                transaction_id: cancelTarget.transaction_id ?? null,
                                stripe_payment_intent_id: null,
                            }}
                            onSuccess={() => {
                                setCancelTarget(null);
                                fetchEntries();
                            }}
                            onCancel={() => setCancelTarget(null)}
                        />
                    }
                />
            )}

            {/* Withdraw from waitlist modal */}
            {withdrawTarget && (
                <Modal
                    isShown={!!withdrawTarget}
                    hide={() => setWithdrawTarget(null)}
                    modalContent={
                        <Confirm
                            message={`Are you sure you want to withdraw ${withdrawTarget.call_name} from the waitlist for ${withdrawTarget.show_type} — ${withdrawTarget.show_name}?`}
                            submitting={withdrawing}
                            buttonsDisabled={withdrawing}
                            onConfirm={handleWithdrawConfirm}
                            onCancel={() => setWithdrawTarget(null)}
                        />
                    }
                />
            )}
        </div>
    );
};

export default RecentEntries;
